/* eslint-disable @typescript-eslint/no-explicit-any */
// CR036: Candidate Signal Builder Component

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Plus, Pencil, Trash2, GripVertical } from 'lucide-react';
import type { CandidateSignal, SignalType } from '@/lib/flyer-pipeline-v2/types';

interface CandidateSignalBuilderProps {
  signals: CandidateSignal[];
  onChange: (signals: CandidateSignal[]) => void;
  disabled?: boolean;
}

const SIGNAL_TYPES: { value: SignalType; label: string; description: string }[] = [
  { value: 'KEYWORD', label: 'Keyword', description: 'Match specific terms' },
  { value: 'REGEX', label: 'Regex', description: 'Pattern matching' },
  { value: 'PROXIMITY', label: 'Proximity', description: 'Terms near each other' },
  { value: 'NEGATIVE_KEYWORDS', label: 'Negative Keywords', description: 'Reduce score when found' },
];

function generateSignalId(): string {
  return `sig_${Date.now().toString(36)}`;
}

function getDefaultParams(type: SignalType): CandidateSignal['params'] {
  switch (type) {
    case 'KEYWORD':
      return { terms: [''], match_mode: 'ANY', case_sensitive: false };
    case 'REGEX':
      return { pattern: '', min_matches: 1 };
    case 'PROXIMITY':
      return { anchor_terms: [''], near_terms: [''], window_chars: 80 };
    case 'NEGATIVE_KEYWORDS':
      return { terms: [''], mode: 'SUBTRACT' };
    default:
      return {};
  }
}

interface SignalEditorProps {
  signal: CandidateSignal | null;
  onSave: (signal: CandidateSignal) => void;
  onCancel: () => void;
}

function SignalEditor({ signal, onSave, onCancel }: SignalEditorProps) {
  const [editedSignal, setEditedSignal] = useState<CandidateSignal>(
    signal || {
      id: generateSignalId(),
      name: '',
      type: 'KEYWORD',
      enabled: true,
      weight: 3,
      params: getDefaultParams('KEYWORD'),
    }
  );

  const updateParam = (key: string, value: unknown) => {
    setEditedSignal({
      ...editedSignal,
      params: { ...editedSignal.params, [key]: value },
    });
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Name</Label>
          <Input
            value={editedSignal.name}
            onChange={(e) => setEditedSignal({ ...editedSignal, name: e.target.value })}
            placeholder="Signal name"
          />
        </div>
        <div className="space-y-2">
          <Label>Type</Label>
          <Select
            value={editedSignal.type}
            onValueChange={(v) =>
              setEditedSignal({
                ...editedSignal,
                type: v as SignalType,
                params: getDefaultParams(v as SignalType),
              })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SIGNAL_TYPES.map((t) => (
                <SelectItem key={t.value} value={t.value}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Weight</Label>
        <Input
          type="number"
          value={editedSignal.weight}
          onChange={(e) => setEditedSignal({ ...editedSignal, weight: parseFloat(e.target.value) || 0 })}
        />
        <p className="text-xs text-muted-foreground">
          Positive = increases candidate score. Negative = decreases score.
        </p>
      </div>

      {/* Type-specific params */}
      {editedSignal.type === 'KEYWORD' && (
        <div className="space-y-2">
          <Label>Terms (comma-separated)</Label>
          <Input
            value={(editedSignal.params as any).terms?.join(', ') || ''}
            onChange={(e) =>
              updateParam(
                'terms',
                e.target.value.split(',').map((t) => t.trim())
              )
            }
            placeholder="gift card, gift cards"
          />
          <div className="flex items-center gap-4 pt-2">
            <div className="flex items-center gap-2">
              <Switch
                checked={(editedSignal.params as any).case_sensitive}
                onCheckedChange={(v) => updateParam('case_sensitive', v)}
              />
              <Label className="text-xs">Case sensitive</Label>
            </div>
            <Select
              value={(editedSignal.params as any).match_mode || 'ANY'}
              onValueChange={(v) => updateParam('match_mode', v)}
            >
              <SelectTrigger className="w-24 h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ANY">Any</SelectItem>
                <SelectItem value="ALL">All</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {editedSignal.type === 'REGEX' && (
        <div className="space-y-2">
          <Label>Pattern</Label>
          <Input
            value={(editedSignal.params as any).pattern || ''}
            onChange={(e) => updateParam('pattern', e.target.value)}
            placeholder="\\$\\s*\\d{1,4}"
          />
          <div className="space-y-1">
            <Label>Min Matches</Label>
            <Input
              type="number"
              min={1}
              value={(editedSignal.params as any).min_matches || 1}
              onChange={(e) => updateParam('min_matches', parseInt(e.target.value) || 1)}
            />
          </div>
        </div>
      )}

      {editedSignal.type === 'PROXIMITY' && (
        <div className="space-y-2">
          <Label>Anchor Terms (comma-separated)</Label>
          <Input
            value={(editedSignal.params as any).anchor_terms?.join(', ') || ''}
            onChange={(e) =>
              updateParam(
                'anchor_terms',
                e.target.value.split(',').map((t) => t.trim())
              )
            }
            placeholder="gift card"
          />
          <Label>Near Terms (comma-separated)</Label>
          <Input
            value={(editedSignal.params as any).near_terms?.join(', ') || ''}
            onChange={(e) =>
              updateParam(
                'near_terms',
                e.target.value.split(',').map((t) => t.trim())
              )
            }
            placeholder="buy, purchase, spend"
          />
          <div className="space-y-1">
            <Label>Window (chars)</Label>
            <Input
              type="number"
              min={10}
              max={500}
              value={(editedSignal.params as any).window_chars || 80}
              onChange={(e) => updateParam('window_chars', parseInt(e.target.value) || 80)}
            />
          </div>
        </div>
      )}

      {editedSignal.type === 'NEGATIVE_KEYWORDS' && (
        <div className="space-y-2">
          <Label>Terms (comma-separated)</Label>
          <Input
            value={(editedSignal.params as any).terms?.join(', ') || ''}
            onChange={(e) =>
              updateParam(
                'terms',
                e.target.value.split(',').map((t) => t.trim())
              )
            }
            placeholder="partner offers, learn more"
          />
          <Select
            value={(editedSignal.params as any).mode || 'SUBTRACT'}
            onValueChange={(v) => updateParam('mode', v)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="SUBTRACT">Subtract (reduce score)</SelectItem>
              <SelectItem value="HARD_BLOCK">Hard Block (disqualify)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      <DialogFooter>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={() => onSave(editedSignal)}>Save Signal</Button>
      </DialogFooter>
    </div>
  );
}

export function CandidateSignalBuilder({ signals, onChange, disabled }: CandidateSignalBuilderProps) {
  const [editingSignal, setEditingSignal] = useState<CandidateSignal | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleToggle = (signalId: string, enabled: boolean) => {
    onChange(signals.map((s) => (s.id === signalId ? { ...s, enabled } : s)));
  };

  const handleDelete = (signalId: string) => {
    onChange(signals.filter((s) => s.id !== signalId));
  };

  const handleSave = (signal: CandidateSignal) => {
    const exists = signals.find((s) => s.id === signal.id);
    if (exists) {
      onChange(signals.map((s) => (s.id === signal.id ? signal : s)));
    } else {
      onChange([...signals, signal]);
    }
    setEditingSignal(null);
    setIsDialogOpen(false);
  };

  const getTypeBadgeColor = (type: SignalType) => {
    switch (type) {
      case 'KEYWORD':
        return 'bg-blue-100 text-blue-800';
      case 'REGEX':
        return 'bg-purple-100 text-purple-800';
      case 'PROXIMITY':
        return 'bg-green-100 text-green-800';
      case 'NEGATIVE_KEYWORDS':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-3">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-8"></TableHead>
            <TableHead className="w-10">On</TableHead>
            <TableHead>Name</TableHead>
            <TableHead className="w-24">Type</TableHead>
            <TableHead className="w-16 text-right">Weight</TableHead>
            <TableHead className="w-20"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {signals.map((signal) => (
            <TableRow key={signal.id} className={!signal.enabled ? 'opacity-50' : ''}>
              <TableCell>
                <GripVertical className="h-4 w-4 text-muted-foreground cursor-move" />
              </TableCell>
              <TableCell>
                <Switch
                  checked={signal.enabled}
                  onCheckedChange={(v) => handleToggle(signal.id, v)}
                  disabled={disabled}
                />
              </TableCell>
              <TableCell className="font-medium text-xs">{signal.name}</TableCell>
              <TableCell>
                <Badge variant="secondary" className={`text-xs ${getTypeBadgeColor(signal.type)}`}>
                  {signal.type}
                </Badge>
              </TableCell>
              <TableCell className="text-right">
                <span className={signal.weight < 0 ? 'text-red-600' : 'text-green-600'}>
                  {signal.weight > 0 ? '+' : ''}
                  {signal.weight}
                </span>
              </TableCell>
              <TableCell>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={() => {
                      setEditingSignal(signal);
                      setIsDialogOpen(true);
                    }}
                    disabled={disabled}
                  >
                    <Pencil className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-destructive"
                    onClick={() => handleDelete(signal.id)}
                    disabled={disabled}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={() => setEditingSignal(null)}
            disabled={disabled}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Signal
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingSignal ? 'Edit Signal' : 'Add Signal'}</DialogTitle>
          </DialogHeader>
          <SignalEditor
            signal={editingSignal}
            onSave={handleSave}
            onCancel={() => {
              setEditingSignal(null);
              setIsDialogOpen(false);
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
